import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMagic, 
    faEdit, 
    faDownload, 
    faShareAlt, 
    faArrowRight, 
    faSpinner,
    faArrowLeft, 
    faCopy,
    faTimes,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { drawOnCanvas } from './canvasUtils';
// Add missing Firebase imports
import { doc, setDoc } from "firebase/firestore";

const Step3 = ({
    gridSize,
    quiltGrid,
    handleEditClick,
    handleDownloadClick,
    handleShareClick,
    handleAIClick,
    loadingAI,
    aiGeneratedLayouts,
    isPremiumUser,
    userCountry,
    calculatePrice,
    currentUser,
    storage,
    nextStep,
    logoSrc,
    prevStep,
    db, // Add db parameter for sharing
    layoutSuggestions, // Add layoutSuggestions parameter
    layout_suggestions // Add layout_suggestions parameter (alternative naming)
}) => {
    // Local canvas reference
    const canvasRef = useRef(null);
    // Track if canvas has been drawn already to prevent multiple redraws
    const [canvasKey, setCanvasKey] = useState(Date.now());
    const hasDrawnRef = useRef(false);
    // Add state for payment redirection
    const [isRedirecting, setIsRedirecting] = useState(false);
    
    // Add local state for share modal and link
    const [localShowModal, setLocalShowModal] = useState(false);
    const [localShareLink, setLocalShareLink] = useState("");
    const [isSharing, setIsSharing] = useState(false);

    // Add state to track payment success
    const [paymentSuccess, setPaymentSuccess] = useState(false);

    // Add state for tracking progressive messages during generation
    const [generationMessageIndex, setGenerationMessageIndex] = useState(0);
    
    // Define array of messages to cycle through
    const generationMessages = [
        "Analyzing your quilt colors and patterns...",
        "Mixing creative combinations...",
        "Preparing fresh layout ideas for you...",
        "Arranging tiles in artistic patterns...",
        "Finalizing your custom layouts..."
    ];
    
    // Set up effect to cycle through messages during loading
    useEffect(() => {
        let messageTimer;
        
        // Only cycle messages when not redirecting for payment but still loading
        if (loadingAI && !(isRedirecting && !isPremiumUser)) {
            messageTimer = setInterval(() => {
                setGenerationMessageIndex(prev => 
                    (prev + 1) % generationMessages.length
                );
            }, 2000); // Change message every 3 seconds
        }
        
        return () => {
            if (messageTimer) clearInterval(messageTimer);
        };
    }, [loadingAI, isRedirecting, isPremiumUser]);
    
    // Reset message index when loading starts
    useEffect(() => {
        if (loadingAI) {
            setGenerationMessageIndex(0);
        }
    }, [loadingAI]);

    // Calculate price format outside the JSX to avoid infinite re-renders
    // Check for both layoutSuggestions and layout_suggestions
    const hasLayoutSuggestions = (layoutSuggestions && layoutSuggestions.length > 0) || 
                               (layout_suggestions && layout_suggestions.length > 0);
    const hasAILayouts = aiGeneratedLayouts && aiGeneratedLayouts.length > 0;
    
    // Only show pricing if user doesn't already have layouts or suggestions
    const priceText = (isPremiumUser || hasLayoutSuggestions || hasAILayouts) ? 
        '' : 
        (gridSize ? ` (${calculatePrice(gridSize, userCountry).priceFormatted})` : '');
    
    // Handle edit click with special canvas refresh logic
    const handleEditButtonClick = () => {
        // Call the parent's handleEditClick function
        handleEditClick();
        
        // Set a flag to force canvas redraw after edit is completed
        hasDrawnRef.current = false;
        
        // Generate a new canvas key to force remounting of the canvas element
        // This will be used when edit mode closes
        const newKey = Date.now();
        console.log("Setting new canvas key for future redraw:", newKey);
        setCanvasKey(newKey);
    };

    // This useEffect handles adding/removing blur effect on the background
    // when the payment modal is open (detected via isRedirecting && !isPremiumUser)
    useEffect(() => {
        if (isRedirecting && !isPremiumUser) {
            // Add blur class to body
            document.body.classList.add('payment-modal-open');
            
            // Create a style element for the blur effect if it doesn't exist
            const blurStyleId = 'payment-modal-blur-style';
            if (!document.getElementById(blurStyleId)) {
                const styleEl = document.createElement('style');
                styleEl.id = blurStyleId;
                styleEl.textContent = `
                    .payment-modal-open .modal-blur-target {
                        filter: blur(4px);
                        transition: filter 0.3s ease;
                    }
                    .payment-modal-open .fixed:not(.payment-modal) {
                        filter: blur(4px);
                    }
                `;
                document.head.appendChild(styleEl);
            }
            
            // Add the blur target class to the main container
            document.querySelector('.max-w-5xl')?.classList.add('modal-blur-target');
        } else {
            // Remove blur when modal closes
            document.body.classList.remove('payment-modal-open');
            document.querySelector('.max-w-5xl')?.classList.remove('modal-blur-target');
        }
        
        // Cleanup function
        return () => {
            document.body.classList.remove('payment-modal-open');
            document.querySelector('.max-w-5xl')?.classList.remove('modal-blur-target');
        };
    }, [isRedirecting, isPremiumUser]);
    
    // Enhanced AI button click handler
    const handleEnhancedAIClick = async () => {
        // If user already has suggestions, skip payment and go directly to next step
        if (hasLayoutSuggestions) {
            console.log("User already has layout suggestions - skipping payment and going to suggestions");
            nextStep();
            return;
        }

        // Set redirecting state to show loading UI
        setIsRedirecting(true);
        
        try {
            // Save a flag in sessionStorage to track that AI processing was started
            sessionStorage.setItem("aiProcessingStarted", "true");
            sessionStorage.setItem("aiProcessingTimestamp", Date.now().toString());
            
            // Call the parent's handleAIClick function
            const result = await handleAIClick();
            
            // Note: handleAIClick should return information about whether payment is needed
            console.log("AI click result:", result);
            
            // If the result indicates payment was needed, keep the redirecting state
            if (result && result.showPaymentModal) {
                console.log("Payment modal should be displayed now");
                
                // Set up a safety timer to reset the UI state after a reasonable timeout
                // This will handle cases where the modal might be closed without proper result
                const safetyTimer = setTimeout(() => {
                    console.log("Safety timer triggered to reset redirecting state");
                    setIsRedirecting(false);
                }, 500);
                
                // Create a MutationObserver to detect when the payment modal is removed from DOM
                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
                            // Check if the removed node might be our modal
                            mutation.removedNodes.forEach(node => {
                                if (node.classList && 
                                    (node.classList.contains('fixed') || 
                                     node.classList.contains('inset-0'))) {
                                    console.log("Payment modal removed from DOM - resetting state");
                                    setIsRedirecting(false);
                                    observer.disconnect();
                                    clearTimeout(safetyTimer);
                                }
                            });
                        }
                    });
                });
                
                // Observe the body for any removed modal elements
                observer.observe(document.body, { childList: true, subtree: true });
                
                // Clean up observer after a reasonable timeout
                setTimeout(() => {
                    observer.disconnect();
                }, 60000); // Disconnect after 1 minute
            } else if (result && result.canceled) {
                // Payment was canceled by the user - reset the redirecting state
                console.log("Payment was canceled by the user");
                setIsRedirecting(false);
                // Clear the processing flags since we're done or canceled
                sessionStorage.removeItem("aiProcessingStarted");
                sessionStorage.removeItem("aiProcessingTimestamp");
            } else if (result && result.success) {
                // If payment was successful, show success message
                setPaymentSuccess(true);
                setIsRedirecting(false);
                
                // Save a persistent flag indicating successful payment
                sessionStorage.setItem("aiPaymentSuccessful", "true");
                
                // Hide success message after 5 seconds
                setTimeout(() => {
                    setPaymentSuccess(false);
                }, 5000);
            } else {
                // If no payment was needed or payment completed, reset redirecting state
                console.log("Resetting redirecting state - no specific condition met");
                setIsRedirecting(false);
            }
        } catch (error) {
            console.error("Error during AI click:", error);
            // Show user-friendly error if it's an AI/Anthropic error
            if (
                error.message &&
                (
                    error.message.includes('Anthropic') ||
                    error.message.includes('Claude') ||
                    error.message.includes('overloaded') ||
                    error.message.includes('429') ||
                    error.message.includes('timeout') ||
                    error.message.includes('rate limit') ||
                    error.message.includes('temporarily unavailable')
                )
            ) {
                alert("Our AI service is currently busy or unavailable. Please try again after some time.");
            }
            setIsRedirecting(false);
        }
    };

    // Single useEffect to handle canvas drawing with debouncing to prevent multiple redraws
    useEffect(() => {
        if (!canvasRef.current || !quiltGrid || !quiltGrid.length || !gridSize) {
            console.log("Missing required data for canvas rendering:", {
                hasCanvasRef: !!canvasRef.current,
                hasQuiltGrid: !!quiltGrid,
                quiltGridLength: quiltGrid?.length || 0,
                gridSize
            });
            return;
        }
        
        // Use a flag to ensure we're not redrawing unnecessarily
        if (hasDrawnRef.current) {
            console.log("Canvas already drawn, skipping redraw");
            return;
        }

        // Log the grid size and dimensions interpretation
        const [cols, rows] = gridSize.split('x').map(Number);
        console.log(`Drawing canvas in Step3 with key: ${canvasKey}`);
        console.log(`Grid size ${gridSize} interpreted as ${cols} columns × ${rows} rows`);
        console.log(`Total tiles in quiltGrid: ${quiltGrid.length}`);
        
        // Try to extract tile dimensions from the first tile if available
        let tileWidth, tileHeight;
        let allTilesHaveSameDimensions = true;
        
        if (quiltGrid.length > 0) {
            // Check if tiles have consistent dimensions
            const firstTile = quiltGrid[0];
            tileWidth = firstTile.width;
            tileHeight = firstTile.height;
            
            if (tileWidth && tileHeight) {
                console.log(`Sample tile dimensions from quilt grid: ${tileWidth}x${tileHeight}`);
                
                // Verify all tiles have the same dimensions
                for (let i = 1; i < quiltGrid.length; i++) {
                    if (quiltGrid[i].width !== tileWidth || quiltGrid[i].height !== tileHeight) {
                        allTilesHaveSameDimensions = false;
                        console.warn("Not all tiles have the same dimensions");
                        break;
                    }
                }
                
                if (allTilesHaveSameDimensions) {
                    console.log("All tiles have uniform dimensions - will use native dimensions for rendering");
                }
            }
        }
        
        // Get dimensions from sessionStorage if available
        let tileWidthPixels, tileHeightPixels;
        try {
            const sessionData = JSON.parse(sessionStorage.getItem('firestoreSessionData'));
            if (sessionData && sessionData.quiltDimensions) {
                tileWidthPixels = sessionData.quiltDimensions.tileWidthPixels;
                tileHeightPixels = sessionData.quiltDimensions.tileHeightPixels;
                console.log(`Dimensions from sessionStorage: ${tileWidthPixels}x${tileHeightPixels}`);
                
                // If tiles all have the same dimensions and they match sessionStorage, prefer the tile dimensions
                if (allTilesHaveSameDimensions && 
                    Math.abs(tileWidth - tileWidthPixels) < 10 && 
                    Math.abs(tileHeight - tileHeightPixels) < 10) {
                    console.log("Tile dimensions match sessionStorage dimensions - using tile dimensions");
                    tileWidthPixels = tileWidth;
                    tileHeightPixels = tileHeight;
                }
            }
        } catch (err) {
            console.error("Error parsing sessionStorage data:", err);
        }
        
        // Verify expected number of tiles
        const expectedTiles = cols * rows;
        if (quiltGrid.length !== expectedTiles) {
            console.warn(`Warning: Grid size ${gridSize} expects ${expectedTiles} tiles but has ${quiltGrid.length} tiles`);
        }
        
        // Log a sample of the quilt grid to verify structure
        if (quiltGrid.length > 0) {
            const sample = quiltGrid[0];
            console.log(`Sample quiltGrid item:`, sample);
            
            // Verify position format
            if (sample?.position && Array.isArray(sample.position)) {
                const [row, col] = sample.position;
                if (row >= rows || col >= cols) {
                    console.warn(`Warning: Sample tile position [${row},${col}] is out of bounds for grid size ${cols}x${rows}`);
                }
            }
        }
        
        // Use a delay to ensure the canvas is fully available in the DOM
        const timer = setTimeout(() => {
            try {
                // Set the flag before drawing to prevent re-entry
                hasDrawnRef.current = true;
                
                // Draw the canvas with enhanced tile size for basic layout
                // Pass tile dimensions from tile data to use native dimensions when available
                drawOnCanvas(
                    canvasRef, 
                    quiltGrid, 
                    gridSize, 
                    logoSrc, 
                    true, // isBasicLayout
                    tileWidthPixels, 
                    tileHeightPixels
                );
                console.log("Canvas successfully drawn");
            } catch (error) {
                console.error("Error drawing on canvas:", error);
                // Reset flag if drawing fails to allow retry
                hasDrawnRef.current = false;
            }
        }, 200); // Increased delay to ensure DOM is ready
        
        return () => {
            clearTimeout(timer);
        };
    }, [quiltGrid, gridSize, logoSrc, canvasKey]); // Added canvasKey dependency
    
    // Reset the drawn flag if critical dependencies change
    useEffect(() => {
        hasDrawnRef.current = false;
    }, [quiltGrid, gridSize]);

    // Wrapper function for share button to handle local state
    const handleShareButtonClick = () => {
        setIsSharing(true);
        setLocalShowModal(true);
        setLocalShareLink("");
        
        // Call the actual share function with proper parameters
        if (handleShareClick && canvasRef.current) {
            try {
                handleShareClick(
                    { current: canvasRef.current }, // Pass canvas ref in the expected format
                    setLocalShowModal,
                    setLocalShareLink,
                    currentUser,
                    storage,
                    db
                );
            } catch (error) {
                console.error("Error in share functionality:", error);
                setLocalShareLink("Error creating share link. Please try again.");
                setIsSharing(false);
            }
        } else {
            console.error("Cannot share: Missing canvas reference or share function");
            setLocalShareLink("Error: Could not generate share link");
            setIsSharing(false);
        }
    };
    
    // Simple local ShareModal component
    const LocalShareModal = () => {
        if (!localShowModal) return null;
        
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold" style={{ fontFamily: "Playfair Display" }}>
                            Share Your Quilt
                        </h3>
                        <button 
                            onClick={() => setLocalShowModal(false)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    
                    {!localShareLink ? (
                        <div className="flex justify-center items-center py-6">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                            <p className="ml-3 text-gray-600">Creating share link...</p>
                        </div>
                    ) : localShareLink.startsWith("Error") ? (
                        <div className="bg-red-50 p-3 rounded text-red-700 mb-4">
                            {localShareLink}
                        </div>
                    ) : (
                        <>
                            <p className="mb-3 text-gray-600 text-sm">
                                Share this link with others to view your quilt design:
                            </p>
                            <div className="flex items-center mb-4">
                                <input 
                                    type="text" 
                                    value={localShareLink} 
                                    readOnly 
                                    className="flex-1 p-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(localShareLink);
                                        alert("Link copied to clipboard!");
                                    }}
                                    className="bg-[#5B7A8C] hover:bg-[#496779] text-white px-3 py-2 rounded-r-md"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </>
                    )}
                    
                    <div className="flex justify-end">
                        <button 
                            onClick={() => setLocalShowModal(false)}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Add state to track if layout has been saved
    const [savedToFirestore, setSavedToFirestore] = useState(false);
    
    // Save the current layout to Firestore
    const saveLayoutToFirestore = async () => {
        if (!currentUser?.uid || !quiltGrid || quiltGrid.length === 0) return;
        
        try {
            // Create a document reference for this user's basic layout
            const layoutRef = doc(db, "user_layouts", currentUser.uid);
            
            // Save the layout data
            await setDoc(layoutRef, {
                updatedAt: new Date(),
                userId: currentUser.uid,
                gridSize,
                quiltGrid,
                type: "basic"
            }, { merge: true });
            
            console.log("Saved basic layout to Firestore");
            setSavedToFirestore(true);
        } catch (error) {
            console.error("Error saving layout:", error);
        }
    };
    
    // Save layout when quilt grid changes
    useEffect(() => {
        if (quiltGrid && quiltGrid.length > 0) {
            // Use debounce to avoid too many saves
            const saveTimer = setTimeout(() => {
                saveLayoutToFirestore();
            }, 1000);
            
            return () => clearTimeout(saveTimer);
        }
    }, [quiltGrid]);

    return (
        <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                <FontAwesomeIcon icon={faMagic} className="mr-2" />
                Your Basic Quilt Layout
            </h2>
            
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                This is your basic {gridSize} quilt layout. You can download this design or explore AI-powered layout suggestions.
            </p>
            
            {/* Quilt preview */}
            <div className="mb-4 sm:mb-6 border rounded-lg p-3 sm:p-4 bg-gray-50">
                <div className="quilt-container max-w-full overflow-hidden flex justify-center items-center bg-white p-2 sm:p-4 border rounded">
                    <div className="w-full flex justify-center items-center">
                        <canvas 
                            key={canvasKey} // Use key to force remounting when needed
                            ref={canvasRef} 
                            className="max-w-full h-auto object-contain"
                            id="step3-canvas"
                            style={{ maxHeight: 'calc(90vh - 100px)' }} // Limit height on laptop screens
                        ></canvas>
                    </div>
                </div>
            </div>
            
            {/* Action buttons - Updated with new colors */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-4">
                <button
                    onClick={handleEditButtonClick}
                    className="bg-[#8B6F47] hover:bg-[#7A6040] text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base"
                >
                    <FontAwesomeIcon icon={faEdit} className="mr-1 sm:mr-2" />
                    <span className="hidden xs:inline">Edit Layout</span>
                    <span className="xs:hidden">Edit</span>
                </button>
                
                <button
                    onClick={() => handleDownloadClick(canvasRef, gridSize, currentUser, storage)}
                    className="bg-[#2D8B47] hover:bg-[#256E39] text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base"
                >
                    <FontAwesomeIcon icon={faDownload} className="mr-1 sm:mr-2" />
                    <span>Download</span>
                </button>
                
                <button
                    onClick={handleShareButtonClick}
                    className="bg-[#5B7A8C] hover:bg-[#496779] text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg flex items-center text-sm sm:text-base"
                >
                    <FontAwesomeIcon icon={faShareAlt} className="mr-1 sm:mr-2" />
                    <span>Share</span>
                </button>
            </div>
            
            {/* AI Layout option - Show different UI based on whether layouts exist */}
            <div className="border-t border-gray-200 pt-4 sm:pt-6 mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                    Want More Layout Options?
                </h3>
                
                {paymentSuccess && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                        <h4 className="text-lg font-semibold text-green-700 mb-2" style={{ fontFamily: "Playfair Display" }}>
                            Ready to Generate Your Layouts? 🟢
                        </h4>
                        <p className="text-green-700 mb-3">
                            Your payment for the AI layout pack was successful!
                            Click the button below to generate your 5 personalized quilt layouts.
                        </p>
                        <p className="text-sm text-gray-600">
                            These layouts are created using your uploaded clothes' colors, patterns, and themes.
                            You'll see them appear in just a few seconds.
                        </p>
                    </div>
                )}
                
                <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">
                    {hasAILayouts 
                        ? "Your AI layouts are ready! You can view, edit or download them."
                        : hasLayoutSuggestions
                        ? "Your layout suggestions are ready! View and generate AI layouts based on them."
                        : "Generate AI-powered layout suggestions based on your clothing items' colors, patterns, and themes. With the paid layout pack, you can also add borders/bindings, text, and include uncut (embossed) clothes in your quilt design. Note: AI layouts are generated based on your input and may need manual tweaks."}
                </p>
                
                <button
                    onClick={isRedirecting || loadingAI ? undefined : handleEnhancedAIClick}
                    disabled={loadingAI || isRedirecting || hasAILayouts}
                    className={`w-full py-3 rounded-lg flex items-center justify-center font-semibold ${
                        loadingAI || isRedirecting || hasAILayouts
                            ? 'bg-gray-400 cursor-not-allowed text-white'
                            : 'bg-[#9CAF88] hover:bg-[#8A9B79] text-white'
                    }`}
                >
                    {loadingAI || isRedirecting ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                            <span className="text-sm sm:text-base">
                                {isRedirecting && !isPremiumUser && !hasLayoutSuggestions
                                    ? "Preparing Payment..." 
                                    : generationMessages[generationMessageIndex]}
                            </span>
                        </>
                    ) : hasAILayouts ? (
                        <>
                            <FontAwesomeIcon icon={faMagic} className="mr-2" />
                            <span className="text-sm sm:text-base">AI Layouts Ready - View Now</span>
                        </>
                    ) : hasLayoutSuggestions ? (
                        <>
                            <FontAwesomeIcon icon={faMagic} className="mr-2" />
                            <span className="text-sm sm:text-base">View Layout Suggestions</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faMagic} className="mr-2" />
                            <span className="text-sm sm:text-base">Generate 5 AI-Enhanced Layouts{priceText}</span>
                        </>
                    )}
                </button>
            </div>
            
            {/* Navigation buttons with new colors */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-4 sm:mt-6">
                <button
                    onClick={() => {
                        console.log("Back button clicked in Step 3");
                        // Clear the stayOnStep2 flag when intentionally navigating back
                        sessionStorage.removeItem("stayOnStep2");
                        // Always go to step 2 first, not directly to step 1
                        prevStep();
                    }}
                    className="order-2 sm:order-1 w-full sm:w-auto px-4 py-2 bg-[#B8A99A] hover:bg-[#A69688] text-white rounded-lg flex items-center justify-center sm:justify-start"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to Images
                </button>
                
                {(hasAILayouts || hasLayoutSuggestions) && (
                    <button
                        onClick={nextStep}
                        className="order-1 sm:order-2 w-full sm:w-auto px-4 py-2 bg-[#9CAF88] hover:bg-[#8A9B79] text-white rounded-lg flex items-center justify-center sm:justify-end"
                    >
                        {hasAILayouts ? "View AI Layouts" : "View Suggestions"} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </button>
                )}
            </div>
            
            {/* Add the local share modal */}
            {localShowModal && <LocalShareModal />}
        </div>
    );
};

export default Step3;
